<template>
  <div v-show="visible" class="agent-chat-widget" ref="widgetRoot" :style="widgetStyle">
    <!-- pointerdown en vez de mousedown: así se arrastra también con el dedo en tablet y móvil. -->
    <button class="agent-chat-fab" :class="{ 'agent-chat-fab-open': isOpen }" @pointerdown="fabStartDrag" @click="onFabClick">
      <i class="f7-icons">chat_bubble_2_fill</i>
    </button>

    <transition name="agent-chat-panel">
      <div v-if="isOpen" class="agent-chat-panel" :class="{ 'agent-chat-panel-maximized': maximized }" :style="panelStyle">
        <div class="agent-chat-header" @mousedown.prevent="startDrag" @dblclick.prevent="toggleMaximize">
          <div>
            <div class="agent-chat-title">Asistente SIMECAL</div>
            <div class="agent-chat-subtitle">{{ activeView === 'history' ? 'Tus conversaciones guardadas' : 'Consultas funcionales y de negocio' }}</div>
            <div v-if="activeView === 'chat' && currentConversationTitle" class="agent-chat-conversation-name" :title="currentConversationTitle">
              {{ currentConversationTitle }}
            </div>
          </div>
          <div class="agent-chat-actions">
            <button class="agent-chat-header-btn" @click.stop="toggleConversationList" :title="activeView === 'history' ? 'Volver al chat' : 'Conversaciones'">
              <i class="material-icons">{{ activeView === 'history' ? 'chat' : 'history' }}</i>
            </button>
            <button class="agent-chat-header-btn" @click.stop="startNewConversation" title="Nueva conversacion">
              <i class="f7-icons">plus_circle_fill</i>
            </button>
            <button class="agent-chat-header-btn" @click.stop="toggleMaximize" :title="maximized ? 'Restaurar' : 'Maximizar'">
              <i class="material-icons">{{ maximized ? 'fullscreen_exit' : 'fullscreen' }}</i>
            </button>
            <button class="agent-chat-header-btn" @click.stop="toggleChat" title="Cerrar">
              <i class="f7-icons">xmark_circle_fill</i>
            </button>
          </div>
        </div>

        <div v-if="activeView === 'history'" class="agent-chat-history">
          <div class="agent-chat-history-toolbar">
            <button class="agent-chat-history-action" @click="startNewConversation">
              <i class="f7-icons">arrow_clockwise_circle</i>
              <span>Nueva conversacion</span>
            </button>
            <button class="agent-chat-history-action agent-chat-history-action-secondary" @click="loadConversations">
              <i class="material-icons">refresh</i>
              <span>Actualizar</span>
            </button>
          </div>

          <div v-if="conversationsLoading" class="agent-chat-history-empty">
            Cargando conversaciones...
          </div>

          <div v-else-if="conversationsError" class="agent-chat-history-error">
            {{ conversationsError }}
          </div>

          <div v-else-if="!conversations.length" class="agent-chat-history-empty">
            Aun no tienes conversaciones guardadas.
          </div>

          <div v-else class="agent-chat-history-list">
            <div
              v-for="conversation in conversations"
              :key="conversation.conversationId"
              class="agent-chat-history-item"
              :class="{ 'agent-chat-history-item-active': conversation.conversationId === conversationId }"
            >
              <div class="agent-chat-history-item-top">
                <div class="agent-chat-history-item-main" @click="openConversation(conversation)">
                  <div class="agent-chat-history-item-title">{{ conversation.title || 'Conversacion sin titulo' }}</div>
                  <div class="agent-chat-history-item-summary">{{ conversation.summary || 'Sin resumen disponible' }}</div>
                  <div class="agent-chat-history-item-meta">
                    <span>{{ formatConversationDate(conversation.updatedAt || conversation.createdAt) }}</span>
                    <span v-if="conversation.executionStats && conversation.executionStats.totalExecutions">{{ conversation.executionStats.totalExecutions }} consultas</span>
                  </div>
                </div>

                <div class="agent-chat-history-item-actions" @mousedown.stop.prevent @click.stop>
                  <button
                    type="button"
                    class="agent-chat-history-icon-btn"
                    title="Renombrar conversacion"
                    @click.stop.prevent="renameConversation(conversation)"
                  >
                    <i class="material-icons">edit</i>
                  </button>
                  <button
                    type="button"
                    class="agent-chat-history-icon-btn agent-chat-history-icon-btn-delete"
                    title="Eliminar conversacion"
                    @click.stop.prevent="deleteConversation(conversation)"
                  >
                    <i class="material-icons">delete_outline</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else ref="messagesContainer" class="agent-chat-messages">
          <div v-if="!messages.length" class="agent-chat-empty">
            <div class="agent-chat-empty-title">Haz una pregunta al asistente</div>
            <div class="agent-chat-empty-text">Ejemplo: Que tenia planificado para el 09/02/2026</div>
          </div>

          <div v-for="message in messages" :key="message.id" class="agent-chat-message" :class="'agent-chat-message-' + message.role">
            <div class="agent-chat-bubble">
              <div class="agent-chat-message-text" v-html="renderMessageHtml(message)"></div>
              <div v-if="message.sources && message.sources.length" class="agent-chat-message-sources">
                <button
                  type="button"
                  class="agent-chat-message-sources-toggle"
                  @click="toggleMessageSources(message)"
                >
                  <span class="agent-chat-message-sources-title">Fuentes documentales</span>
                  <span class="agent-chat-message-sources-toggle-icon">{{ message.sourcesOpen ? 'Ocultar' : 'Mostrar' }}</span>
                </button>
                <div v-if="message.sourcesOpen" class="agent-chat-message-sources-list">
                  <div
                    v-for="source in message.sources"
                    :key="message.id + '-' + source.tag + '-' + source.version_id + '-' + source.page"
                    class="agent-chat-message-source-item"
                  >
                    <button
                      type="button"
                      class="agent-chat-message-source-button"
                      @click="toggleSourcePreview(message, source)"
                    >
                      <span class="agent-chat-message-source-tag">{{ source.tag }}</span>
                      <span class="agent-chat-message-source-text">{{ formatDocumentSource(source) }}</span>
                    </button>
                    <div v-if="source.previewOpen" class="agent-chat-source-preview">
                      <div v-if="source.previewLoading" class="agent-chat-source-preview-loading">Cargando fuente...</div>
                      <div v-else-if="source.previewError" class="agent-chat-source-preview-error">{{ source.previewError }}</div>
                      <div
                        v-else-if="source.previewType === 'pdf' && source.previewUrl"
                        class="agent-chat-source-preview-pdf"
                      >
                        <div class="agent-chat-source-preview-toolbar">
                          <button
                            type="button"
                            class="agent-chat-source-preview-nav-btn"
                            :disabled="(source.previewPage || 1) <= 1"
                            @click="source.previewPage = Math.max(1, (source.previewPage || 1) - 1)"
                          >
                            Anterior
                          </button>
                          <span class="agent-chat-source-preview-page">
                            Pág. {{ source.previewPage || 1 }} / {{ source.previewPageCount || 1 }}
                          </span>
                          <button
                            type="button"
                            class="agent-chat-source-preview-nav-btn"
                            :disabled="(source.previewPage || 1) >= (source.previewPageCount || 1)"
                            @click="source.previewPage = Math.min(source.previewPageCount || 1, (source.previewPage || 1) + 1)"
                          >
                            Siguiente
                          </button>
                        </div>
                        <pdf
                          :src="source.previewUrl"
                          :page="source.previewPage || 1"
                          class="agent-chat-source-preview-pdf-canvas"
                          @num-pages="source.previewPageCount = $event"
                          @page-loaded="source.previewPage = $event"
                        ></pdf>
                        <div class="agent-chat-source-preview-toolbar agent-chat-source-preview-toolbar-bottom">
                          <button
                            type="button"
                            class="agent-chat-source-preview-nav-btn"
                            :disabled="(source.previewPage || 1) <= 1"
                            @click="source.previewPage = Math.max(1, (source.previewPage || 1) - 1)"
                          >
                            Anterior
                          </button>
                          <span class="agent-chat-source-preview-page">
                            Pág. {{ source.previewPage || 1 }} / {{ source.previewPageCount || 1 }}
                          </span>
                          <button
                            type="button"
                            class="agent-chat-source-preview-nav-btn"
                            :disabled="(source.previewPage || 1) >= (source.previewPageCount || 1)"
                            @click="source.previewPage = Math.min(source.previewPageCount || 1, (source.previewPage || 1) + 1)"
                          >
                            Siguiente
                          </button>
                        </div>
                      </div>
                      <img
                        v-else-if="source.previewType === 'image' && source.previewUrl"
                        :src="source.previewUrl"
                        class="agent-chat-source-preview-image"
                      />
                      <pre
                        v-else-if="source.previewType === 'text'"
                        class="agent-chat-source-preview-text"
                      >{{ source.previewText }}</pre>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="message.pendingActions && message.pendingActions.length" class="agent-chat-pending-actions">
                <div v-if="message.pendingActions.length > 1" class="agent-chat-pending-actions-batch-header">
                  <span class="agent-chat-pending-actions-title">{{ message.pendingActions.length }} propuestas de modificación</span>
                  <div class="agent-chat-pending-actions-batch-buttons">
                    <button
                      type="button"
                      class="agent-chat-pending-action-btn-secondary"
                      :disabled="isBatchBusy(message)"
                      @click="toggleAllPendingActionsSelection(message)"
                    >
                      Marcar/Desmarcar todas
                    </button>
                    <button
                      type="button"
                      class="agent-chat-pending-action-btn agent-chat-pending-action-apply"
                      :disabled="isBatchBusy(message) || countSelectedPendingActions(message) === 0"
                      @click="applyAllPendingActions(message)"
                    >
                      Aplicar seleccionadas ({{ countSelectedPendingActions(message) }}/{{ countActivePendingActions(message) }})
                    </button>
                    <button
                      type="button"
                      class="agent-chat-pending-action-btn agent-chat-pending-action-discard"
                      :disabled="isBatchBusy(message)"
                      @click="discardAllPendingActions(message)"
                    >
                      Descartar todas
                    </button>
                  </div>
                </div>
                <div
                  v-for="pa in message.pendingActions"
                  :key="pa.id"
                  class="agent-chat-pending-action"
                  :class="'agent-chat-pending-action-' + (pa.uiStatus || 'pending')"
                >
                  <div class="agent-chat-pending-action-header">
                    <label
                      v-if="message.pendingActions.length > 1 && (!pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error')"
                      class="agent-chat-pending-action-check"
                      :title="pa.selected !== false ? 'Incluir en Aplicar seleccionadas' : 'Excluir de Aplicar seleccionadas'"
                    >
                      <input
                        type="checkbox"
                        :checked="pa.selected !== false"
                        :disabled="pa.busy"
                        @change="pa.selected = $event.target.checked; persistState()"
                      />
                    </label>
                    <span class="agent-chat-pending-action-title">Propuesta de modificación</span>
                    <span class="agent-chat-pending-action-coll">{{ pa.collection }}</span>
                  </div>
                  <div class="agent-chat-pending-action-filter">
                    <strong>_id:</strong>
                    <span>{{ pa.filter && pa.filter._id }}</span>
                  </div>
                  <div v-if="pa.reason" class="agent-chat-pending-action-reason">{{ pa.reason }}</div>
                  <table v-if="pa.diff && pa.diff.length" class="agent-chat-pending-action-diff">
                    <thead>
                      <tr>
                        <th>Campo</th>
                        <th>Antes</th>
                        <th>Después</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="entry in pa.diff" :key="entry.path">
                        <td>
                          <span v-if="entry.label" class="agent-chat-pending-action-label">{{ entry.label }}</span>
                          <small class="agent-chat-pending-action-path">{{ entry.path }}</small>
                        </td>
                        <td class="agent-chat-pending-action-before">{{ formatDiffValue(entry.before) }}</td>
                        <td class="agent-chat-pending-action-after">{{ formatDiffValue(entry.after) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="pa.uiStatus === 'executed'" class="agent-chat-pending-action-result agent-chat-pending-action-result-ok">
                    ✓ Cambio aplicado.
                  </div>
                  <div v-else-if="pa.uiStatus === 'discarded'" class="agent-chat-pending-action-result agent-chat-pending-action-result-discarded">
                    ✕ Propuesta descartada.
                  </div>
                  <div v-else-if="pa.uiStatus === 'error' && pa.errorMessage" class="agent-chat-pending-action-result agent-chat-pending-action-result-error">
                    {{ pa.errorMessage }}
                  </div>
                  <div v-if="!pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error'" class="agent-chat-pending-action-buttons">
                    <button
                      type="button"
                      class="agent-chat-pending-action-btn agent-chat-pending-action-apply"
                      :disabled="pa.busy"
                      @click="applyPendingAction(pa)"
                    >
                      Aplicar
                    </button>
                    <button
                      type="button"
                      class="agent-chat-pending-action-btn agent-chat-pending-action-discard"
                      :disabled="pa.busy"
                      @click="discardPendingActionUI(pa)"
                    >
                      Descartar
                    </button>
                  </div>
                </div>
                <div v-if="message.pendingActionsRejected && message.pendingActionsRejected.length" class="agent-chat-pending-actions-rejected">
                  <div class="agent-chat-pending-actions-rejected-title">
                    {{ message.pendingActionsRejected.length }} propuesta(s) rechazada(s) por el backend
                  </div>
                  <ul>
                    <li v-for="r in message.pendingActionsRejected" :key="r.index">
                      #{{ r.index }}<span v-if="r.filterId"> (_id: {{ r.filterId }})</span>: {{ r.error }}
                    </li>
                  </ul>
                </div>
              </div>
              <div v-if="message.meta" class="agent-chat-message-meta">{{ message.meta }}</div>
            </div>
          </div>

          <div v-if="loading" class="agent-chat-message agent-chat-message-assistant">
            <div class="agent-chat-bubble agent-chat-bubble-loading">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <div v-if="errorMessage && activeView === 'chat'" class="agent-chat-error">{{ errorMessage }}</div>

        <div v-if="activeView === 'chat'" class="agent-chat-composer">
          <textarea
            v-model="draft"
            class="agent-chat-textarea"
            placeholder="Escribe tu consulta..."
            rows="1"
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact.stop
          ></textarea>
          <button class="agent-chat-send" :disabled="loading || !canSend" @click="sendMessage">
            <i class="f7-icons">arrow_up_circle_fill</i>
          </button>
        </div>
        <div v-if="activeView === 'chat' && modeloAgente" class="agent-chat-model">Modelo: {{ modeloAgente }}</div>
        <div v-if="activeView === 'chat'" class="agent-chat-conservacion">{{ avisoConservacion }}</div>

        <div class="agent-chat-resizer" @mousedown.prevent="startResize" title="Redimensionar"></div>
      </div>
    </transition>
  </div>
</template>

<script>
import AgentChat from '../js/db/AgentChat';
import cerrojoDeLaSesion from '../js/utils/cerrojoDeLaSesion.js';
import DOMPurify from 'dompurify';
import pdf from 'vue-pdf';

const STORAGE_KEY = 'simecal-agent-chat-state';
const DIA_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'AgentChatWidget',
  components: {
    pdf,
  },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      draft: '',
      conversationId: null,
      currentConversationTitle: '',
      messages: [],
      errorMessage: '',
      modeloAgente: '',
      diasConservacion: null,
      // null: aún no se sabe; false: simecal-ai dice que el borrado de #933 no está activado (#1233).
      conservacionActiva: null,
      tituloDesde: null,
      activeView: 'chat',
      conversations: [],
      conversationsLoading: false,
      conversationsError: '',
      sequence: 0,
      panelWidth: 360,
      panelHeight: 620,
      panelLeft: null,
      panelTop: null,
      maximized: false,
      previousBounds: null,
      resizeState: null,
      dragState: null,
      fabLeft: null,
      fabTop: null,
      fabDragState: null,
      suppressFabClick: false,
    };
  },
  computed: {
    canSend() {
      return this.draft && this.draft.trim().length > 0;
    },
    avisoConservacion() {
      // Mientras el borrado no esté activado no se puede prometer que la conversación se borre sola.
      if (this.conservacionActiva === false) {
        return 'Esta conversación se guarda en el servidor. El borrado automático que ha aprobado '
          + 'Dirección todavía no está activado: de momento no se borra sola.';
      }
      const plazo = this.diasConservacion ? ('durante ' + this.diasConservacion + ' días') : 'durante un tiempo limitado';
      return 'Esta conversación se guarda ' + plazo + ' y después se borra sola. '
        + 'Nadie más la lee salvo con autorización escrita de Dirección, y esa lectura queda registrada.';
    },
    panelStyle() {
      if (this.isMobileViewport()) {
        return {};
      }

      return {
        width: this.panelWidth + 'px',
        height: this.panelHeight + 'px',
        left: this.panelLeft + 'px',
        top: this.panelTop + 'px',
      };
    },
    widgetStyle() {
      // Si el usuario ha movido el globo, posicionamos el contenedor con left/top;
      // si no, dejamos el anclaje por defecto (abajo-derecha) del CSS.
      if (this.fabLeft != null && this.fabTop != null) {
        return { left: this.fabLeft + 'px', top: this.fabTop + 'px', right: 'auto', bottom: 'auto' };
      }
      return {};
    },
  },
  mounted() {
    this.restoreState();
    if (this.isOpen) {
      this.loadModelo();
      this.loadPolitica();
    }
    window.addEventListener('resize', this.onViewportResize);
    window.addEventListener('beforeunload', this.beforeUnloadCleanup);
  },
  beforeDestroy() {
    this.beforeUnloadCleanup();
    window.removeEventListener('resize', this.onViewportResize);
    window.removeEventListener('beforeunload', this.beforeUnloadCleanup);
    this.stopResize();
    this.stopDrag();
    window.removeEventListener('pointermove', this.fabOnDrag);
    window.removeEventListener('pointerup', this.fabStopDrag);
    window.removeEventListener('pointercancel', this.fabStopDrag);
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        // La pestaña puede llevar días abierta: lo que caducó mientras tanto no se vuelve a enseñar.
        this.caducarCopiaLocal();
        this.ensurePanelPosition();
        this.loadConversations();
        this.loadModelo();
        this.loadPolitica();
      }
      this.$nextTick(this.scrollToBottom);
    },
    toggleConversationList() {
      this.activeView = this.activeView === 'history' ? 'chat' : 'history';
      if (this.activeView === 'history') {
        this.loadConversations();
      } else {
        this.$nextTick(this.scrollToBottom);
      }
      this.persistState();
    },
    startNewConversation() {
      this.conversationId = null;
      this.currentConversationTitle = '';
      this.tituloDesde = null;
      this.messages = [];
      this.errorMessage = '';
      this.activeView = 'chat';
      this.persistState();
      this.$nextTick(this.scrollToBottom);
    },
    sendMessage() {
      const question = this.draft && this.draft.trim();
      if (!question || this.loading) {
        return;
      }

      this.errorMessage = '';
      this.pushMessage('user', question);
      this.draft = '';
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `finally` que lo soltaba.
      cerrojoDeLaSesion(this, 'loading');
      this.$nextTick(this.scrollToBottom);

      AgentChat.ask({
        data: {
          question,
          conversationId: this.conversationId,
          context: this.buildRequestContext(),
          maxIterations: 10,
          debug: false,
        },
      }).then((response) => {
        this.conversationId = response.conversationId || this.conversationId;
        if (response.title) {
          this.currentConversationTitle = response.title;
          // El título sale de la primera pregunta: caduca con ella, no con la última.
          this.tituloDesde = this.tituloDesde || new Date().toISOString();
        }

        const pendingActions = this.collectPendingActions(response);
        const pendingActionsRejected = Array.isArray(response.pendingActionsRejected)
          ? response.pendingActionsRejected
          : [];

        if (response.answer) {
          this.pushMessage('assistant', response.answer, this.buildMeta(response), response.documentSources || [], pendingActions, pendingActionsRejected);
        } else if (response.clarification) {
          this.pushMessage('assistant', response.clarification, this.buildMeta(response), [], pendingActions, pendingActionsRejected);
        } else if (pendingActions.length) {
          const defaultMsg = pendingActions.length > 1
            ? 'He preparado varias propuestas de cambio. Revísalas y aplica las que quieras conservar.'
            : 'He preparado una propuesta de cambio. Revísala antes de aplicarla.';
          this.pushMessage('assistant', defaultMsg, this.buildMeta(response), [], pendingActions, pendingActionsRejected);
        } else {
          this.pushMessage('assistant', 'No se ha recibido una respuesta valida del asistente.');
        }

        this.persistState();
        this.loadConversations();
      }).catch((error) => {
        this.errorMessage = error && error.message
          ? error.message
          : 'Ha habido un error consultando el asistente.';
      }).finally(() => {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      });
    },
    // El plazo lo fija la política de simecal-ai (#933): se pide en vez de escribirlo aquí para que
    // el aviso no pueda decir un número y el borrado aplicar otro.
    // Se pide en cada apertura, como el modelo, y no solo la primera vez: el plazo guardado en el
    // navegador podría haberse cambiado en el servidor.
    loadPolitica() {
      AgentChat.getPolitica().then((datos) => {
        // Publicada pero no activada (#1233): no hay plazo en vigor, así que la copia de este
        // navegador tampoco se caduca. Se guarda para que la próxima apertura no recorte con un
        // plazo de antes mientras llega la respuesta.
        if (datos && datos.activa === false) {
          this.conservacionActiva = false;
          this.diasConservacion = null;
          this.persistState();
          return;
        }
        if (datos && datos.diasConservacion) {
          // Se guarda también al pasar a activa o cambiar el plazo: caducarCopiaLocal solo guarda si
          // quita algo, y sin esto una recarga volvería al «no activada» o al plazo de antes.
          const cambia = this.conservacionActiva !== true || this.diasConservacion !== datos.diasConservacion;
          this.conservacionActiva = true;
          this.diasConservacion = datos.diasConservacion;
          if (cambia) {
            this.persistState();
          }
          this.caducarCopiaLocal();
        }
      }).catch(() => {
        // Se queda el último plazo conocido; sin ninguno, el aviso sale sin el número.
      });
    },
    // La copia de la conversación que guarda este navegador cumple el mismo plazo que el servidor
    // (#933): el aviso promete que el texto se borra solo, y esta copia también es texto. Se mira
    // mensaje a mensaje porque el servidor caduca turno a turno. Lo que no tiene fecha es de antes
    // de que se guardara y no se puede saber su edad: se quita, igual que el servidor oculta lo
    // dudoso. Sin plazo conocido no se quita nada, porque no hay con qué comparar.
    caducarCopiaLocal() {
      if (this.conservacionActiva === false || !this.diasConservacion) {
        return;
      }
      const limite = Date.now() - this.diasConservacion * DIA_MS;
      const vigente = (fecha) => {
        const instante = fecha ? Date.parse(fecha) : NaN;
        return !Number.isNaN(instante) && instante >= limite;
      };
      const quedan = (this.messages || []).filter((message) => vigente(message.fecha));
      const tituloCaducado = !!this.currentConversationTitle && !vigente(this.tituloDesde);
      if (quedan.length === (this.messages || []).length && !tituloCaducado) {
        return;
      }
      this.messages = quedan;
      if (tituloCaducado) {
        this.currentConversationTitle = '';
        this.tituloDesde = null;
      }
      if (!quedan.length) {
        // Sin nada que enseñar, seguir colgado de esa conversación dejaría un chat vacío que
        // continúa otra; se empieza una nueva, que es lo que vería tras el borrado del servidor.
        this.conversationId = null;
        this.currentConversationTitle = '';
        this.tituloDesde = null;
      }
      this.persistState();
    },
    // Se pide cada vez que se abre el chat: el modelo se cambia en el catálogo sin desplegar.
    loadModelo() {
      // Se vacía antes de preguntar: mientras llega la respuesta no hay que seguir enseñando el
      // modelo anterior, que puede haber cambiado en el catálogo.
      this.modeloAgente = '';
      // Si se cierra y reabre el chat antes de que conteste, hay dos peticiones en vuelo: solo la
      // última puede tocar el valor, o una vieja que llegue tarde (o falle) pisaría a la buena.
      const peticion = (this.modeloPeticion || 0) + 1;
      this.modeloPeticion = peticion;
      AgentChat.getModelo().then((datos) => {
        if (peticion === this.modeloPeticion) {
          this.modeloAgente = (datos && (datos.etiqueta || datos.modelo)) || '';
        }
      }).catch(() => {
        // Si no llega, simplemente no se muestra: no impide usar el chat.
        if (peticion === this.modeloPeticion) {
          this.modeloAgente = '';
        }
      });
    },
    buildMeta(response) {
      const parts = [];
      if (response.status === 'clarify') {
        parts.push('Necesita aclaracion');
      }
      if (response.iterations) {
        parts.push(response.iterations + ' iteraciones');
      }
      if (response.queriesExecuted && response.queriesExecuted.length) {
        parts.push(response.queriesExecuted.length + ' queries');
      }
      if (response.metrics && response.metrics.model) {
        parts.push(response.metrics.model);
      }
      return parts.join(' · ');
    },
    formatDocumentSource(source) {
      if (!source) {
        return '';
      }

      const title = source.title || source.filename || source.version_id || 'Documento';
      const page = Number(source.page || 0);
      const endPage = Number(source.end_page || page || 0);

      if (!page) {
        return title;
      }

      if (endPage && endPage !== page) {
        return title + ' (páginas ' + page + '-' + endPage + ')';
      }

      return title + ' (página ' + page + ')';
    },
    toggleMessageSources(message) {
      if (!message) {
        return;
      }

      message.sourcesOpen = !message.sourcesOpen;
      this.$forceUpdate();
    },
    toggleSourcePreview(message, source) {
      if (!message || !source) {
        return;
      }

      if (source.previewOpen) {
        this.resetSourcePreviewState(source);
        this.$forceUpdate();
        return;
      }

      this.closeOtherSourcePreviews(message, source);
      source.previewOpen = true;

      if (source.previewLoaded) {
        this.$forceUpdate();
        return;
      }

      source.previewLoading = true;
      source.previewError = '';
      this.$forceUpdate();

      AgentChat.getDocumentSourcePreview({
        data: {
          path: source.path,
          source_type: source.source_type,
          page: source.page,
          end_page: source.end_page,
          title: source.title,
          filename: source.filename,
          version_id: source.version_id,
          content: source.content,
          excerpt: source.excerpt,
          descripcion: source.descripcion,
        },
      }).then((preview) => {
        if (preview.sourceType === 'pdf' || preview.sourceType === 'image') {
          this.revokeSourcePreviewUrl(source);
          source.previewType = preview.sourceType;
          source.previewUrl = this.createObjectUrlFromBase64(preview.data, preview.mimeType);
          source.previewText = '';
          if (preview.sourceType === 'pdf') {
            source.previewPage = 1;
            source.previewPageCount = 1;
          }
        } else {
          this.revokeSourcePreviewUrl(source);
          source.previewType = 'text';
          source.previewUrl = '';
          source.previewText = preview.text || '';
        }
        source.previewLoaded = true;
      }).catch((error) => {
        source.previewError = error && error.message
          ? error.message
          : 'No se ha podido cargar la fuente.';
      }).finally(() => {
        source.previewLoading = false;
        this.$forceUpdate();
      });
    },
    closeOtherSourcePreviews(message, activeSource) {
      (message.sources || []).forEach((source) => {
        if (source !== activeSource) {
          source.previewOpen = false;
        }
      });
    },
    resetSourcePreviewState(source) {
      this.revokeSourcePreviewUrl(source);
      source.previewOpen = false;
      source.previewLoading = false;
      source.previewError = '';
      source.previewLoaded = false;
      source.previewType = '';
      source.previewText = '';
      source.previewPage = 1;
      source.previewPageCount = 1;
    },
    createObjectUrlFromBase64(base64, mimeType) {
      const binary = window.atob(base64 || '');
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      const blob = new Blob([bytes], { type: mimeType || 'application/octet-stream' });
      return window.URL.createObjectURL(blob);
    },
    revokeSourcePreviewUrl(source) {
      if (source && source.previewUrl && String(source.previewUrl).indexOf('blob:') === 0) {
        window.URL.revokeObjectURL(source.previewUrl);
      }
      if (source) {
        source.previewUrl = '';
      }
    },
    openSourcePreview(source) {
      if (source && source.previewUrl) {
        window.open(source.previewUrl, '_blank', 'noopener');
      }
    },
    beforeUnloadCleanup() {
      (this.messages || []).forEach((message) => {
        (message.sources || []).forEach((source) => {
          this.revokeSourcePreviewUrl(source);
        });
      });
    },
    sanitizeSourcesForStorage(sources) {
      return (Array.isArray(sources) ? sources : []).map((source) => ({
        tag: source.tag,
        document_id: source.document_id,
        version_id: source.version_id,
        title: source.title,
        filename: source.filename,
        path: source.path,
        page: source.page,
        end_page: source.end_page,
        section: source.section,
        source_type: source.source_type,
        unit_kind: source.unit_kind,
        score: source.score,
      }));
    },
    escapeHtml(text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    renderMarkdownLinks(text) {
      return String(text || '').replace(/\[([^\]]+)\]\(((?:https?:\/\/|#!\/|\/)[^\s)]+)\)/g, (match, label, url) => {
        const baseUrl = window.location.href.split('#')[0];
        let href = String(url || '').trim();
        const anchorAttrs = 'class="external" external target="_blank" rel="noopener noreferrer"';

        if (href.startsWith('#!/http://') || href.startsWith('#!/https://')) {
          href = href.slice(3);
        }

        if (href.startsWith('/http://') || href.startsWith('/https://')) {
          href = href.slice(1);
        }

        if (href.startsWith('http://') || href.startsWith('https://')) {
          return '<a href="' + href + '" ' + anchorAttrs + '>' + label + '</a>';
        }

        if (href.startsWith('#!/')) {
          href = baseUrl + href;
        } else if (href.startsWith('/')) {
          href = baseUrl + '#!' + href;
        }

        return '<a href="' + href + '" ' + anchorAttrs + '>' + label + '</a>';
      });
    },
    renderInlineMarkdown(text) {
      return this.renderMarkdownLinks(this.escapeHtml(text)
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/__([^_]+)__/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/_([^_]+)_/g, '<em>$1</em>'));
    },
    isTableLine(line) {
      return /^\|.+\|$/.test(line.trim());
    },
    isTableSeparator(line) {
      return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());
    },
    parseTable(lines, startIndex) {
      const headerLine = lines[startIndex] || '';
      const separatorLine = lines[startIndex + 1] || '';

      if (!this.isTableLine(headerLine) || !this.isTableSeparator(separatorLine)) {
        return null;
      }

      const rows = [];
      let index = startIndex + 2;
      while (index < lines.length && this.isTableLine(lines[index])) {
        rows.push(lines[index]);
        index += 1;
      }

      const splitRow = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());
      const headers = splitRow(headerLine);
      const bodyRows = rows.map(splitRow);

      let html = '<table><thead><tr>';
      headers.forEach((header) => {
        html += '<th>' + this.renderInlineMarkdown(header) + '</th>';
      });
      html += '</tr></thead><tbody>';
      bodyRows.forEach((row) => {
        html += '<tr>';
        headers.forEach((_, cellIndex) => {
          html += '<td>' + this.renderInlineMarkdown(row[cellIndex] || '') + '</td>';
        });
        html += '</tr>';
      });
      html += '</tbody></table>';

      return {
        html,
        nextIndex: index,
      };
    },
    parseList(lines, startIndex) {
      const unorderedPattern = /^[-*]\s+(.+)$/;
      const orderedPattern = /^\d+\.\s+(.+)$/;
      const firstLine = lines[startIndex] || '';
      const isOrdered = orderedPattern.test(firstLine.trim());
      const matcher = isOrdered ? orderedPattern : unorderedPattern;

      if (!matcher.test(firstLine.trim())) {
        return null;
      }

      let index = startIndex;
      let html = isOrdered ? '<ol>' : '<ul>';
      while (index < lines.length) {
        const line = (lines[index] || '').trim();
        const match = line.match(matcher);
        if (!match) {
          break;
        }
        html += '<li>' + this.renderInlineMarkdown(match[1]) + '</li>';
        index += 1;
      }
      html += isOrdered ? '</ol>' : '</ul>';

      return {
        html,
        nextIndex: index,
      };
    },
    parseHeading(line) {
      const match = String(line || '').trim().match(/^(#{1,3})\s+(.+)$/);

      if (!match) {
        return null;
      }

      const level = match[1].length;
      return '<h' + level + '>' + this.renderInlineMarkdown(match[2]) + '</h' + level + '>';
    },
    renderAssistantMarkdown(text) {
      const normalized = String(text || '').replace(/\r\n/g, '\n');
      const lines = normalized.split('\n');
      const blocks = [];

      for (let index = 0; index < lines.length;) {
        const line = lines[index] || '';
        const trimmed = line.trim();

        if (!trimmed) {
          index += 1;
          continue;
        }

        const table = this.parseTable(lines, index);
        if (table) {
          blocks.push(table.html);
          index = table.nextIndex;
          continue;
        }

        const list = this.parseList(lines, index);
        if (list) {
          blocks.push(list.html);
          index = list.nextIndex;
          continue;
        }

        const heading = this.parseHeading(line);
        if (heading) {
          blocks.push(heading);
          index += 1;
          continue;
        }

        const paragraphLines = [];
        while (index < lines.length) {
          const nextLine = lines[index] || '';
          const nextTrimmed = nextLine.trim();
          if (!nextTrimmed || this.parseTable(lines, index) || this.parseList(lines, index) || this.parseHeading(nextLine)) {
            break;
          }
          paragraphLines.push(this.renderInlineMarkdown(nextTrimmed));
          index += 1;
        }

        if (paragraphLines.length) {
          blocks.push('<p>' + paragraphLines.join('<br>') + '</p>');
        }

        if (!paragraphLines.length) {
          index += 1;
        }
      }

      return blocks.join('');
    },
    renderMessageHtml(message) {
      const text = message && message.text ? String(message.text) : '';

      if (message && message.role === 'assistant') {
        return DOMPurify.sanitize(this.renderAssistantMarkdown(text));
      }

      return this.escapeHtml(text).replace(/\n/g, '<br>');
    },
    pushMessage(role, text, meta, sources, pendingActions, pendingActionsRejected) {
      this.sequence += 1;
      const actionsArray = this.normalizePendingActionsInput(pendingActions).map((pa) => ({
        ...pa,
        uiStatus: 'pending',
        busy: false,
        errorMessage: '',
        selected: true,
      }));
      this.messages.push({
        id: role + '-' + this.sequence,
        role,
        text,
        fecha: new Date().toISOString(),
        meta: meta || '',
        sourcesOpen: false,
        sources: this.sanitizeSourcesForStorage(sources).map((source) => ({
          ...source,
          previewOpen: false,
          previewLoading: false,
          previewLoaded: false,
          previewError: '',
          previewType: '',
          previewUrl: '',
          previewText: '',
          previewPage: 1,
          previewPageCount: 1,
        })),
        pendingActions: actionsArray,
        pendingActionsRejected: Array.isArray(pendingActionsRejected) ? pendingActionsRejected.slice() : [],
      });
      this.persistState();
      this.$nextTick(this.scrollToBottom);
    },
    normalizePendingActionsInput(input) {
      if (!input) return [];
      if (Array.isArray(input)) return input.filter((pa) => pa && pa.id);
      if (input && input.id) return [input];
      return [];
    },
    collectPendingActions(response) {
      const result = [];
      if (Array.isArray(response.pendingActions)) {
        response.pendingActions.forEach((pa) => { if (pa && pa.id) result.push(pa); });
      }
      if (response.pendingAction && response.pendingAction.id) {
        result.push(response.pendingAction);
      }
      return result;
    },
    countActivePendingActions(message) {
      if (!message || !Array.isArray(message.pendingActions)) return 0;
      return message.pendingActions.filter((pa) => !pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error').length;
    },
    countSelectedPendingActions(message) {
      if (!message || !Array.isArray(message.pendingActions)) return 0;
      return message.pendingActions.filter((pa) =>
        pa.selected !== false && (!pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error')
      ).length;
    },
    isBatchBusy(message) {
      if (!message || !Array.isArray(message.pendingActions)) return false;
      return message.pendingActions.some((pa) => pa.busy);
    },
    toggleAllPendingActionsSelection(message) {
      if (!message || !Array.isArray(message.pendingActions)) return;
      const active = message.pendingActions.filter((pa) => !pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error');
      const allSelected = active.length > 0 && active.every((pa) => pa.selected !== false);
      active.forEach((pa) => { pa.selected = !allSelected; });
      this.persistState();
    },
    buildRequestContext() {
      try {
        const route = this.$f7 && this.$f7.view && this.$f7.view.current && this.$f7.view.current.router
          ? this.$f7.view.current.router.currentRoute
          : null;
        const url = route && route.url ? route.url : '';
        const match = url.match(/^\/inspecciones\/(view|edit|view-offline|edit-offline)\/([^/]+)\//);
        if (match) {
          return {
            currentEntity: 'expedientes',
            currentExpedienteId: decodeURIComponent(match[2]),
            currentRoute: url,
          };
        }
      } catch (e) {
        // silent: si falla la detección, seguimos con contexto vacío
      }
      return {};
    },
    formatDiffValue(value) {
      if (value === undefined) return '(vacío)';
      if (value === null) return 'null';
      if (typeof value === 'object') {
        try { return JSON.stringify(value); } catch (e) { return String(value); }
      }
      return String(value);
    },
    applyPendingAction(pa) {
      if (!pa || !pa.id || pa.busy) return Promise.resolve();
      pa.busy = true;
      pa.errorMessage = '';
      return AgentChat.executePendingAction({ itemId: pa.id })
        .then(() => {
          pa.uiStatus = 'executed';
          pa.busy = false;
          this.persistState();
        })
        .catch((error) => {
          pa.busy = false;
          pa.uiStatus = 'error';
          pa.errorMessage = (error && error.message) ? error.message : 'No se ha podido aplicar la propuesta.';
          this.persistState();
        });
    },
    discardPendingActionUI(pa) {
      if (!pa || !pa.id || pa.busy) return Promise.resolve();
      pa.busy = true;
      return AgentChat.discardPendingAction({ itemId: pa.id })
        .then(() => {
          pa.uiStatus = 'discarded';
          pa.busy = false;
          this.persistState();
        })
        .catch(() => {
          pa.uiStatus = 'discarded';
          pa.busy = false;
          this.persistState();
        });
    },
    async applyAllPendingActions(message) {
      if (!message || !Array.isArray(message.pendingActions)) return;
      const pending = message.pendingActions.filter((pa) =>
        pa.selected !== false && (!pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error')
      );
      for (const pa of pending) {
        // Secuencial para evitar conflictos de concurrencia 409 entre documentos del mismo lote.
        await this.applyPendingAction(pa);
      }
    },
    async discardAllPendingActions(message) {
      if (!message || !Array.isArray(message.pendingActions)) return;
      const pending = message.pendingActions.filter((pa) => !pa.uiStatus || pa.uiStatus === 'pending' || pa.uiStatus === 'error');
      await Promise.all(pending.map((pa) => this.discardPendingActionUI(pa)));
    },
    scrollToBottom() {
      if (this.activeView !== 'chat' || !this.$refs.messagesContainer) {
        return;
      }
      this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
    },
    persistState() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        conversationId: this.conversationId,
        currentConversationTitle: this.currentConversationTitle,
        tituloDesde: this.tituloDesde,
        diasConservacion: this.diasConservacion,
        conservacionActiva: this.conservacionActiva,
        messages: (this.messages || []).map((message) => ({
          id: message.id,
          role: message.role,
          text: message.text,
          fecha: message.fecha || null,
          meta: message.meta,
          sourcesOpen: false,
          sources: this.sanitizeSourcesForStorage(message.sources),
          pendingActions: Array.isArray(message.pendingActions)
            ? message.pendingActions.map((pa) => ({
                id: pa.id,
                collection: pa.collection,
                filter: pa.filter,
                update: pa.update,
                reason: pa.reason,
                diff: pa.diff,
                expiresAt: pa.expiresAt,
                uiStatus: pa.uiStatus || 'pending',
                errorMessage: pa.errorMessage || '',
                selected: pa.selected !== false,
              }))
            : [],
          pendingActionsRejected: Array.isArray(message.pendingActionsRejected) ? message.pendingActionsRejected : [],
        })),
        activeView: this.activeView,
        sequence: this.sequence,
        panelWidth: this.panelWidth,
        panelHeight: this.panelHeight,
        panelLeft: this.panelLeft,
        panelTop: this.panelTop,
        maximized: this.maximized,
        previousBounds: this.previousBounds,
        fabLeft: this.fabLeft,
        fabTop: this.fabTop,
      }));
    },
    restoreState() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          return;
        }
        const parsed = JSON.parse(stored);
        this.conversationId = parsed.conversationId || null;
        this.currentConversationTitle = parsed.currentConversationTitle || '';
        this.tituloDesde = parsed.tituloDesde || null;
        // El último plazo conocido, para no enseñar texto caducado mientras llega el del servidor.
        this.diasConservacion = parsed.diasConservacion || null;
        this.conservacionActiva = typeof parsed.conservacionActiva === 'boolean' ? parsed.conservacionActiva : null;
        this.messages = Array.isArray(parsed.messages)
          ? parsed.messages.map((message) => {
              // Backcompat: mensajes antiguos guardaban pendingAction (singular).
              const legacySingle = message.pendingAction ? [message.pendingAction] : [];
              const rawActions = Array.isArray(message.pendingActions) ? message.pendingActions : legacySingle;
              return {
                ...message,
                sourcesOpen: false,
                sources: this.sanitizeSourcesForStorage(message.sources).map((source) => ({
                  ...source,
                  previewOpen: false,
                  previewLoading: false,
                  previewLoaded: false,
                  previewError: '',
                  previewType: '',
                  previewUrl: '',
                  previewText: '',
                  previewPage: 1,
                  previewPageCount: 1,
                })),
                pendingActions: rawActions.map((pa) => ({
                  ...pa,
                  busy: false,
                  uiStatus: pa.uiStatus || 'pending',
                  errorMessage: pa.errorMessage || '',
                  selected: pa.selected !== false,
                })),
                pendingActionsRejected: Array.isArray(message.pendingActionsRejected) ? message.pendingActionsRejected : [],
                pendingAction: undefined,
              };
            })
          : [];
        this.activeView = parsed.activeView === 'history' ? 'history' : 'chat';
        this.sequence = parsed.sequence || this.messages.length || 0;
        this.panelWidth = parsed.panelWidth || 360;
        this.panelHeight = parsed.panelHeight || 620;
        this.panelLeft = typeof parsed.panelLeft === 'number' ? parsed.panelLeft : null;
        this.panelTop = typeof parsed.panelTop === 'number' ? parsed.panelTop : null;
        this.maximized = !!parsed.maximized;
        this.previousBounds = parsed.previousBounds || null;
        this.fabLeft = typeof parsed.fabLeft === 'number' ? parsed.fabLeft : null;
        this.fabTop = typeof parsed.fabTop === 'number' ? parsed.fabTop : null;
        this.caducarCopiaLocal();
      } catch (e) {
        this.conversationId = null;
        this.currentConversationTitle = '';
        this.tituloDesde = null;
        this.messages = [];
        this.activeView = 'chat';
        this.sequence = 0;
        this.panelWidth = 360;
        this.panelHeight = 620;
        this.panelLeft = null;
        this.panelTop = null;
        this.maximized = false;
        this.previousBounds = null;
        this.fabLeft = null;
        this.fabTop = null;
      }
      this.ensurePanelPosition();
      this.$nextTick(this.scrollToBottom);
    },
    loadConversations() {
      cerrojoDeLaSesion(this, 'conversationsLoading');
      this.conversationsError = '';

      return AgentChat.listConversations({
        archived: false,
        limit: 30,
      }).then((conversations) => {
        this.conversations = Array.isArray(conversations) ? conversations : [];
        this.syncCurrentConversationTitle();
      }).catch((error) => {
        this.conversationsError = error && error.message
          ? error.message
          : 'No se han podido cargar las conversaciones.';
      }).finally(() => {
        this.conversationsLoading = false;
      });
    },
    openConversation(conversation) {
      if (!conversation || !conversation.conversationId) {
        return;
      }

      this.conversationsError = '';
      cerrojoDeLaSesion(this, 'loading');

      AgentChat.getConversation({
        itemId: conversation.conversationId,
      }).then((response) => {
        this.conversationId = response.conversationId || conversation.conversationId;
        this.currentConversationTitle = response.title || conversation.title || '';
        // Las mismas fechas con las que el servidor caduca título y turnos.
        this.tituloDesde = response.textoDesde || response.createdAt || null;
        this.messages = this.buildMessagesFromConversation(response);
        this.sequence = this.messages.length;
        this.activeView = 'chat';
        this.errorMessage = '';
        this.persistState();
      }).catch((error) => {
        this.conversationsError = error && error.message
          ? error.message
          : 'No se ha podido abrir la conversacion.';
      }).finally(() => {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      });
    },
    buildMessagesFromConversation(conversation) {
      const turns = Array.isArray(conversation && conversation.turns)
        ? conversation.turns
        : [];

      return turns
        .filter((turn) => turn && turn.role && turn.content)
        .map((turn, index) => ({
          id: (turn.role || 'message') + '-' + (turn.timestamp || index),
          role: turn.role,
          text: turn.content,
          fecha: turn.timestamp || conversation.createdAt || null,
          meta: '',
        }));
    },
    deleteConversation(conversation) {
      if (!conversation || !conversation.conversationId) {
        return;
      }

      if (!window.confirm('Se eliminara la conversacion seleccionada.')) {
        return;
      }

      this.conversationsError = '';

      AgentChat.deleteConversation({
        itemId: conversation.conversationId,
      }).then(() => {
        if (conversation.conversationId === this.conversationId) {
          this.startNewConversation();
        }

        this.conversations = this.conversations.filter(
          (item) => item.conversationId !== conversation.conversationId
        );
      }).catch((error) => {
        this.conversationsError = error && error.message
          ? error.message
          : 'No se ha podido eliminar la conversacion.';
      });
    },
    renameConversation(conversation) {
      if (!conversation || !conversation.conversationId) {
        return;
      }

      const currentTitle = conversation.title || '';
      const nextTitle = window.prompt('Nuevo nombre de la conversacion', currentTitle);

      if (nextTitle === null) {
        return;
      }

      const normalizedTitle = nextTitle.trim();
      if (!normalizedTitle) {
        this.conversationsError = 'El nombre de la conversacion no puede estar vacio.';
        return;
      }

      this.conversationsError = '';

      AgentChat.updateConversation({
        itemId: conversation.conversationId,
        data: {
          title: normalizedTitle,
        },
      }).then((updatedConversation) => {
        if (conversation.conversationId === this.conversationId) {
          this.currentConversationTitle = normalizedTitle;
        }
        this.conversations = this.conversations.map((item) => {
          if (item.conversationId !== conversation.conversationId) {
            return item;
          }

          return {
            ...item,
            ...updatedConversation,
            title: normalizedTitle,
            summary: updatedConversation && updatedConversation.summary
              ? updatedConversation.summary
              : item.summary,
          };
        });

        return this.loadConversations();
      }).catch((error) => {
        this.conversationsError = error && error.message
          ? error.message
          : 'No se ha podido renombrar la conversacion.';
      });
    },
    formatConversationDate(value) {
      if (!value) {
        return '';
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }

      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    syncCurrentConversationTitle() {
      if (!this.conversationId) {
        this.currentConversationTitle = '';
        return;
      }

      const activeConversation = (this.conversations || []).find(
        (conversation) => conversation && conversation.conversationId === this.conversationId
      );

      this.currentConversationTitle = activeConversation && activeConversation.title
        ? activeConversation.title
        : this.currentConversationTitle;
      if (activeConversation && activeConversation.title) {
        this.tituloDesde = activeConversation.textoDesde || activeConversation.createdAt || this.tituloDesde;
      }
    },
    startResize(event) {
      if (this.isMobileViewport() || this.maximized) {
        return;
      }

      const rightEdge = this.panelLeft + this.panelWidth;
      this.resizeState = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: this.panelWidth,
        startHeight: this.panelHeight,
        startLeft: this.panelLeft,
        startTop: this.panelTop,
        rightEdge,
      };

      window.addEventListener('mousemove', this.onResize);
      window.addEventListener('mouseup', this.stopResize);
      document.body.classList.add('agent-chat-resizing');
      document.body.style.cursor = 'nesw-resize';
      document.body.style.userSelect = 'none';
    },
    onResize(event) {
      if (!this.resizeState) {
        return;
      }

      const widthLimit = Math.max(320, window.innerWidth - 24);
      const heightLimit = Math.max(420, window.innerHeight - this.resizeState.startTop - 12);
      const resizedWidth = this.resizeState.startWidth + (this.resizeState.startX - event.clientX);
      const resizedHeight = this.resizeState.startHeight + (event.clientY - this.resizeState.startY);
      const maxWidthByLeft = Math.max(320, this.resizeState.rightEdge - 12);
      const nextWidth = Math.min(Math.max(resizedWidth, 320), Math.min(widthLimit, maxWidthByLeft));
      const nextHeight = Math.min(Math.max(resizedHeight, 420), heightLimit);

      this.panelWidth = nextWidth;
      this.panelHeight = nextHeight;
      this.panelLeft = this.resizeState.rightEdge - nextWidth;
      this.clampPanelPosition();
    },
    stopResize() {
      if (!this.resizeState) {
        return;
      }

      this.resizeState = null;
      window.removeEventListener('mousemove', this.onResize);
      window.removeEventListener('mouseup', this.stopResize);
      document.body.classList.remove('agent-chat-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.persistState();
    },
    clampPanelSize() {
      if (this.isMobileViewport()) {
        this.panelWidth = 360;
        this.panelHeight = 620;
        return;
      }

      const maxWidth = Math.max(320, window.innerWidth - 40);
      const maxHeight = Math.max(420, window.innerHeight - 110);
      this.panelWidth = Math.min(Math.max(this.panelWidth, 320), maxWidth);
      this.panelHeight = Math.min(Math.max(this.panelHeight, 420), maxHeight);
    },
    ensurePanelPosition() {
      this.clampPanelSize();

      if (this.isMobileViewport()) {
        this.panelLeft = null;
        this.panelTop = null;
        return;
      }

      if (this.maximized) {
        this.panelLeft = 12;
        this.panelTop = 12;
        this.panelWidth = Math.max(320, window.innerWidth - 24);
        this.panelHeight = Math.max(420, window.innerHeight - 24);
        return;
      }

      if (typeof this.panelLeft !== 'number') {
        this.panelLeft = Math.max(12, window.innerWidth - this.panelWidth - 20);
      }
      if (typeof this.panelTop !== 'number') {
        this.panelTop = Math.max(12, window.innerHeight - this.panelHeight - 98);
      }

      this.clampPanelPosition();
    },
    clampPanelPosition() {
      if (this.isMobileViewport() || this.maximized) {
        return;
      }

      const maxLeft = Math.max(12, window.innerWidth - this.panelWidth - 12);
      const maxTop = Math.max(12, window.innerHeight - this.panelHeight - 12);
      this.panelLeft = Math.min(Math.max(this.panelLeft, 12), maxLeft);
      this.panelTop = Math.min(Math.max(this.panelTop, 12), maxTop);
    },
    startDrag(event) {
      if (this.isMobileViewport() || this.maximized) {
        return;
      }

      this.dragState = {
        startX: event.clientX,
        startY: event.clientY,
        startLeft: this.panelLeft,
        startTop: this.panelTop,
      };

      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
      document.body.style.cursor = 'move';
      document.body.style.userSelect = 'none';
    },
    onDrag(event) {
      if (!this.dragState) {
        return;
      }

      this.panelLeft = this.dragState.startLeft + (event.clientX - this.dragState.startX);
      this.panelTop = this.dragState.startTop + (event.clientY - this.dragState.startY);
      this.clampPanelPosition();
    },
    stopDrag() {
      if (!this.dragState) {
        return;
      }

      this.dragState = null;
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.persistState();
    },
    onFabClick() {
      // Si acabamos de arrastrar, el click posterior no debe abrir/cerrar el chat.
      if (this.suppressFabClick) {
        this.suppressFabClick = false;
        return;
      }
      this.toggleChat();
    },
    fabStartDrag(event) {
      // El globo se puede mover en cualquier tamaño de pantalla (el panel, no: en móvil va anclado).
      // Solo el botón principal del ratón; el dedo y el lápiz entran como botón 0.
      if (event.button != null && event.button !== 0) {
        return;
      }
      let startLeft = this.fabLeft;
      let startTop = this.fabTop;
      if (startLeft == null || startTop == null) {
        const el = this.$refs.widgetRoot;
        const rect = el
          ? el.getBoundingClientRect()
          : { left: window.innerWidth - 82, top: window.innerHeight - 82 };
        startLeft = rect.left;
        startTop = rect.top;
      }
      this.fabDragState = {
        startX: event.clientX,
        startY: event.clientY,
        startLeft,
        startTop,
        moved: false,
        pointerId: event.pointerId,
      };
      window.addEventListener('pointermove', this.fabOnDrag);
      window.addEventListener('pointerup', this.fabStopDrag);
      window.addEventListener('pointercancel', this.fabStopDrag);
    },
    fabOnDrag(event) {
      if (!this.fabDragState) {
        return;
      }
      // Con varios dedos en pantalla solo sigue al que empezó el arrastre.
      if (event.pointerId != null && this.fabDragState.pointerId != null && event.pointerId !== this.fabDragState.pointerId) {
        return;
      }
      const dx = event.clientX - this.fabDragState.startX;
      const dy = event.clientY - this.fabDragState.startY;
      // Umbral: por debajo de 4px lo tratamos como clic, no como arrastre.
      if (!this.fabDragState.moved && Math.abs(dx) + Math.abs(dy) < 4) {
        return;
      }
      if (!this.fabDragState.moved) {
        this.fabDragState.moved = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }
      this.fabLeft = this.fabDragState.startLeft + dx;
      this.fabTop = this.fabDragState.startTop + dy;
      this.clampFabPosition();
    },
    clampFabPosition() {
      const size = 62;
      const maxLeft = Math.max(8, window.innerWidth - size - 8);
      const maxTop = Math.max(8, window.innerHeight - size - 8);
      if (this.fabLeft != null) {
        this.fabLeft = Math.min(Math.max(this.fabLeft, 8), maxLeft);
      }
      if (this.fabTop != null) {
        this.fabTop = Math.min(Math.max(this.fabTop, 8), maxTop);
      }
    },
    fabStopDrag() {
      if (!this.fabDragState) {
        return;
      }
      const moved = this.fabDragState.moved;
      this.fabDragState = null;
      window.removeEventListener('pointermove', this.fabOnDrag);
      window.removeEventListener('pointerup', this.fabStopDrag);
      window.removeEventListener('pointercancel', this.fabStopDrag);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (moved) {
        this.suppressFabClick = true;
        this.persistState();
      }
    },
    toggleMaximize() {
      if (this.isMobileViewport()) {
        return;
      }

      if (this.maximized) {
        this.maximized = false;
        if (this.previousBounds) {
          this.panelWidth = this.previousBounds.width;
          this.panelHeight = this.previousBounds.height;
          this.panelLeft = this.previousBounds.left;
          this.panelTop = this.previousBounds.top;
        }
      } else {
        this.previousBounds = {
          width: this.panelWidth,
          height: this.panelHeight,
          left: this.panelLeft,
          top: this.panelTop,
        };
        this.maximized = true;
      }

      this.ensurePanelPosition();
      this.persistState();
    },
    onViewportResize() {
      this.ensurePanelPosition();
      this.clampFabPosition();
      this.persistState();
    },
    isMobileViewport() {
      return window.innerWidth <= 768;
    },
  },
};
</script>

<style scoped>
.agent-chat-widget {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 13000;
}

.agent-chat-fab {
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: 0;
  background: linear-gradient(135deg, #22355b 0%, #2f7eb9 100%);
  color: #fff;
  box-shadow: 0 14px 34px rgba(34, 53, 91, 0.34);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;
}

.agent-chat-fab .f7-icons {
  font-size: 30px;
}

.agent-chat-fab-open {
  background: linear-gradient(135deg, #dc6d2e 0%, #f0a23d 100%);
}

.agent-chat-panel {
  position: fixed;
  width: 360px;
  max-width: calc(100vw - 24px);
  height: min(620px, calc(100vh - 110px));
  display: flex;
  flex-direction: column;
  border-radius: 22px;
  overflow: hidden;
  background: #f4f7fb;
  box-shadow: 0 24px 50px rgba(25, 45, 80, 0.28);
  border: 1px solid rgba(34, 53, 91, 0.08);
}

.agent-chat-resizer {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 22px;
  height: 22px;
  cursor: nesw-resize;
  background:
    linear-gradient(225deg, transparent 0%, transparent 45%, rgba(34, 53, 91, 0.15) 45%, rgba(34, 53, 91, 0.15) 55%, transparent 55%),
    linear-gradient(225deg, transparent 0%, transparent 65%, rgba(34, 53, 91, 0.28) 65%, rgba(34, 53, 91, 0.28) 75%, transparent 75%);
}

.agent-chat-header {
  background: linear-gradient(135deg, #22355b 0%, #315f8d 100%);
  color: #fff;
  padding: 16px 18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: move;
}

.agent-chat-title {
  font-size: 16px;
  font-weight: 700;
}

.agent-chat-subtitle {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.85;
}

.agent-chat-conversation-name {
  margin-top: 8px;
  max-width: 220px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-chat-actions {
  display: flex;
  gap: 8px;
}

.agent-chat-header-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  cursor: pointer;
}

.agent-chat-header-btn i {
  font-size: 18px;
  line-height: 1;
}

.agent-chat-panel-maximized .agent-chat-header {
  cursor: default;
}

.agent-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background:
    radial-gradient(circle at top right, rgba(49, 95, 141, 0.12), transparent 30%),
    linear-gradient(180deg, #f6f8fb 0%, #edf2f8 100%);
}

.agent-chat-history {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(circle at top right, rgba(49, 95, 141, 0.12), transparent 30%),
    linear-gradient(180deg, #f6f8fb 0%, #edf2f8 100%);
}

.agent-chat-history-toolbar {
  display: flex;
  gap: 10px;
  padding: 14px 16px 0;
}

.agent-chat-history-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: 999px;
  padding: 8px 12px;
  background: #22355b;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.agent-chat-history-action-secondary {
  background: rgba(34, 53, 91, 0.1);
  color: #22355b;
}

.agent-chat-history-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px 16px;
}

.agent-chat-history-item {
  width: 100%;
  border: 1px solid rgba(34, 53, 91, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  padding: 14px;
  text-align: left;
  cursor: pointer;
  margin-bottom: 10px;
  box-shadow: 0 8px 18px rgba(34, 53, 91, 0.06);
}

.agent-chat-history-item-active {
  border-color: rgba(220, 109, 46, 0.45);
  box-shadow: 0 10px 22px rgba(220, 109, 46, 0.12);
}

.agent-chat-history-item-top {
  display: flex;
  gap: 8px;
  align-items: stretch;
  justify-content: space-between;
}

.agent-chat-history-item-main {
  min-width: 0;
  flex: 1;
  cursor: pointer;
}

.agent-chat-history-item-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
}

.agent-chat-history-item-title {
  color: #22355b;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.agent-chat-history-item-summary {
  margin-top: 8px;
  color: #52657f;
  font-size: 12px;
  line-height: 1.4;
}

.agent-chat-history-item-meta {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #7a8ca6;
  font-size: 11px;
}

.agent-chat-history-icon-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: rgba(34, 53, 91, 0.08);
  color: #22355b;
  cursor: pointer;
  flex: 0 0 auto;
}

.agent-chat-history-icon-btn-delete {
  background: rgba(179, 58, 58, 0.08);
  color: #b33a3a;
}

.agent-chat-history-icon-btn i {
  font-size: 18px;
}

.agent-chat-history-empty,
.agent-chat-history-error {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  color: #52657f;
  font-size: 13px;
}

.agent-chat-history-error {
  color: #b33a3a;
}

.agent-chat-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  color: #4d5f79;
  padding: 0 18px;
}

.agent-chat-empty-title {
  font-weight: 700;
  font-size: 16px;
}

.agent-chat-empty-text {
  margin-top: 10px;
  font-size: 13px;
}

.agent-chat-message {
  display: flex;
  margin-bottom: 12px;
}

.agent-chat-message-user {
  justify-content: flex-end;
}

.agent-chat-message-assistant {
  justify-content: flex-start;
}

.agent-chat-bubble {
  max-width: 88%;
  border-radius: 18px;
  padding: 12px 14px;
  line-height: 1.4;
  font-size: 14px;
  word-break: break-word;
}

.agent-chat-message-user .agent-chat-bubble {
  background: linear-gradient(135deg, #22355b 0%, #2f7eb9 100%);
  color: #fff;
  border-bottom-right-radius: 6px;
}

.agent-chat-message-assistant .agent-chat-bubble {
  background: #fff;
  color: #22355b;
  border-bottom-left-radius: 6px;
  box-shadow: 0 8px 20px rgba(34, 53, 91, 0.08);
}

.agent-chat-message-meta {
  margin-top: 8px;
  font-size: 11px;
  opacity: 0.7;
}

.agent-chat-pending-action {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #f0a000;
  border-radius: 8px;
  background: #fff7e0;
  color: #5a3b00;
  font-size: 12px;
}
.agent-chat-pending-action-executed {
  border-color: #2e7d32;
  background: #e9f5ea;
  color: #1b5e20;
}
.agent-chat-pending-action-discarded {
  border-color: #9e9e9e;
  background: #f5f5f5;
  color: #424242;
  opacity: 0.85;
}
.agent-chat-pending-action-error {
  border-color: #c62828;
  background: #fdecec;
  color: #8a1c1c;
}
.agent-chat-pending-action-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}
.agent-chat-pending-action-title {
  font-weight: 600;
}
.agent-chat-pending-action-coll {
  font-family: monospace;
  font-size: 11px;
  opacity: 0.8;
}
.agent-chat-pending-action-filter {
  font-family: monospace;
  font-size: 11px;
  margin-bottom: 6px;
  word-break: break-all;
}
.agent-chat-pending-action-reason {
  margin-bottom: 6px;
  font-style: italic;
  opacity: 0.9;
}
.agent-chat-pending-action-diff {
  width: 100%;
  border-collapse: collapse;
  margin: 6px 0;
  font-size: 11px;
}
.agent-chat-pending-action-diff th,
.agent-chat-pending-action-diff td {
  padding: 4px 6px;
  border-bottom: 1px solid rgba(90, 59, 0, 0.15);
  text-align: left;
  vertical-align: top;
}
.agent-chat-pending-action-before {
  color: #a14b00;
  word-break: break-word;
}
.agent-chat-pending-action-after {
  color: #1b5e20;
  font-weight: 600;
  word-break: break-word;
}
.agent-chat-pending-action-buttons {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.agent-chat-pending-action-btn {
  border: 0;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
}
.agent-chat-pending-action-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.agent-chat-pending-action-apply {
  background: #f0a000;
  color: #fff;
}
.agent-chat-pending-action-discard {
  background: transparent;
  color: #5a3b00;
  border: 1px solid rgba(90, 59, 0, 0.3);
}
.agent-chat-pending-action-result {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
}

.agent-chat-pending-actions {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.agent-chat-pending-actions-batch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border: 1px dashed #f0a000;
  border-radius: 8px;
  background: #fff2cc;
  color: #5a3b00;
}
.agent-chat-pending-actions-title {
  font-weight: 600;
  font-size: 12px;
}
.agent-chat-pending-actions-batch-buttons {
  display: flex;
  gap: 6px;
}
.agent-chat-pending-action-label {
  display: block;
  font-weight: 600;
  color: #1e1e1e;
}
.agent-chat-pending-action-path {
  display: block;
  font-family: monospace;
  font-size: 10px;
  color: #7a5a00;
  word-break: break-all;
}
.agent-chat-pending-actions-rejected {
  margin-top: 4px;
  padding: 8px 10px;
  border: 1px solid #c62828;
  border-radius: 8px;
  background: #fdecec;
  color: #8a1c1c;
  font-size: 11px;
}
.agent-chat-pending-actions-rejected-title {
  font-weight: 600;
  margin-bottom: 4px;
}
.agent-chat-pending-actions-rejected ul {
  margin: 0;
  padding-left: 18px;
}
.agent-chat-pending-action-check {
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  cursor: pointer;
}
.agent-chat-pending-action-check input[type="checkbox"] {
  width: 14px;
  height: 14px;
  margin: 0;
  cursor: pointer;
}
.agent-chat-pending-action-btn-secondary {
  border: 1px solid rgba(90, 59, 0, 0.3);
  background: transparent;
  color: #5a3b00;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
}
.agent-chat-pending-action-btn-secondary:disabled {
  opacity: 0.6;
  cursor: default;
}

.agent-chat-message-sources {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(34, 53, 91, 0.08);
}

.agent-chat-message-sources-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.agent-chat-message-sources-title {
  font-size: 11px;
  font-weight: 700;
  color: #315f8d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.agent-chat-message-sources-toggle-icon {
  font-size: 11px;
  font-weight: 600;
  color: #6f8198;
  white-space: nowrap;
}

.agent-chat-message-sources-list {
  margin-top: 8px;
}

.agent-chat-message-source-item {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #41556f;
}

.agent-chat-message-source-button {
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.agent-chat-message-source-tag {
  flex: 0 0 auto;
  min-width: 28px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(49, 95, 141, 0.12);
  color: #315f8d;
  font-weight: 700;
  text-align: center;
}

.agent-chat-message-source-text {
  min-width: 0;
  line-height: 1.35;
}

.agent-chat-source-preview {
  margin-top: 10px;
  margin-left: 36px;
  border: 1px solid rgba(34, 53, 91, 0.12);
  border-radius: 12px;
  background: #f7fafd;
  overflow: hidden;
}

.agent-chat-source-preview-loading,
.agent-chat-source-preview-error {
  padding: 12px;
  font-size: 12px;
}

.agent-chat-source-preview-error {
  color: #b33a3a;
}

.agent-chat-source-preview-frame {
  width: 100%;
  height: 360px;
  border: 0;
  display: block;
  background: #fff;
}

.agent-chat-source-preview-pdf {
  background: #fff;
}

.agent-chat-source-preview-toolbar {
  display: grid;
  grid-template-columns: minmax(92px, 1fr) auto minmax(92px, 1fr);
  align-items: center;
  gap: 12px;
  padding: 10px 12px 0;
}

.agent-chat-source-preview-toolbar-bottom {
  padding: 8px 12px 12px;
}

.agent-chat-source-preview-nav-btn {
  border: 0;
  border-radius: 999px;
  background: rgba(49, 95, 141, 0.12);
  color: #315f8d;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.agent-chat-source-preview-nav-btn[disabled] {
  opacity: 0.45;
  cursor: default;
}

.agent-chat-source-preview-page {
  font-size: 12px;
  font-weight: 600;
  color: #41556f;
  white-space: nowrap;
  text-align: center;
}

.agent-chat-source-preview-pdf-canvas {
  display: block;
  width: 100%;
  background: #fff;
  padding: 8px 0 0;
}

.agent-chat-source-preview-image {
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  background: #fff;
}

.agent-chat-source-preview-text {
  margin: 0;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.45;
  color: #22355b;
}

.agent-chat-bubble-loading {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.agent-chat-bubble-loading span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7a8eab;
  animation: agent-chat-blink 1.2s infinite ease-in-out;
}

.agent-chat-bubble-loading span:nth-child(2) {
  animation-delay: 0.15s;
}

.agent-chat-bubble-loading span:nth-child(3) {
  animation-delay: 0.3s;
}

.agent-chat-error {
  padding: 0 16px 12px;
  font-size: 12px;
  color: #b33a3a;
}

.agent-chat-conservacion {
  padding: 0 14px 8px;
  background: #fff;
  font-size: 11px;
  color: #22355b;
  opacity: 0.6;
}

.agent-chat-model {
  padding: 0 14px 8px;
  margin-top: -6px;
  background: #fff;
  font-size: 11px;
  color: #22355b;
  opacity: 0.6;
}

.agent-chat-composer {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding: 14px;
  background: #fff;
  border-top: 1px solid rgba(34, 53, 91, 0.08);
}

.agent-chat-textarea {
  flex: 1;
  resize: none;
  min-height: 46px;
  max-height: 120px;
  border-radius: 16px;
  border: 1px solid rgba(34, 53, 91, 0.15);
  padding: 12px 14px;
  font-size: 14px;
  font-family: inherit;
  background: #f8fafc;
}

.agent-chat-send {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 0;
  background: #dc6d2e;
  color: #fff;
  cursor: pointer;
}

.agent-chat-send[disabled] {
  opacity: 0.45;
  cursor: default;
}

.agent-chat-send .f7-icons {
  font-size: 26px;
}

.agent-chat-panel-enter-active,
.agent-chat-panel-leave-active {
  transition: all 0.22s ease;
}

.agent-chat-panel-enter,
.agent-chat-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.96);
}

@keyframes agent-chat-blink {
  0%, 80%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

@media (max-width: 768px) {
  .agent-chat-widget {
    right: 14px;
    bottom: 14px;
  }

  .agent-chat-panel {
    position: absolute;
    width: min(100vw - 16px, 390px);
    right: -2px;
    bottom: 74px;
    left: auto !important;
    top: auto !important;
    height: min(72vh, 620px);
  }

  .agent-chat-resizer {
    display: none;
  }
}
</style>

<style>
.agent-chat-widget .agent-chat-message-text {
  white-space: normal;
}

.agent-chat-widget .agent-chat-message-text > *:first-child {
  margin-top: 0;
}

.agent-chat-widget .agent-chat-message-text > *:last-child {
  margin-bottom: 0;
}

.agent-chat-widget .agent-chat-message-text h1,
.agent-chat-widget .agent-chat-message-text h2,
.agent-chat-widget .agent-chat-message-text h3 {
  font-weight: 700;
  line-height: 1.25;
}

.agent-chat-widget .agent-chat-message-text h1 {
  margin: 0 0 12px;
  font-size: 18px;
  color: #dc6d2e;
  letter-spacing: 0.01em;
}

.agent-chat-widget .agent-chat-message-text h2 {
  margin: 2px 0 12px;
  font-size: 16px;
  color: #1f5f8b;
  padding-left: 10px;
  border-left: 3px solid rgba(31, 95, 139, 0.25);
}

.agent-chat-widget .agent-chat-message-text h3 {
  margin: 2px 0 10px;
  font-size: 15px;
  color: #2f7eb9;
}

.agent-chat-widget .agent-chat-message-text p,
.agent-chat-widget .agent-chat-message-text ul,
.agent-chat-widget .agent-chat-message-text ol,
.agent-chat-widget .agent-chat-message-text pre,
.agent-chat-widget .agent-chat-message-text table,
.agent-chat-widget .agent-chat-message-text blockquote {
  margin: 0 0 10px;
}

.agent-chat-widget .agent-chat-message-text ul,
.agent-chat-widget .agent-chat-message-text ol {
  padding-left: 18px;
}

.agent-chat-widget .agent-chat-message-text li + li {
  margin-top: 4px;
}

.agent-chat-widget .agent-chat-message-text strong {
  font-weight: 700;
}

.agent-chat-widget .agent-chat-message-text code {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  background: rgba(34, 53, 91, 0.08);
  padding: 1px 5px;
  border-radius: 6px;
}

.agent-chat-widget .agent-chat-message-text pre {
  background: #eef3f8;
  border-radius: 10px;
  padding: 10px 12px;
  overflow-x: auto;
}

.agent-chat-widget .agent-chat-message-text pre code {
  background: transparent;
  padding: 0;
}

.agent-chat-widget .agent-chat-message-text table {
  width: 100%;
  border-collapse: collapse;
  display: block;
  overflow-x: auto;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 10px;
}

.agent-chat-widget .agent-chat-message-text th,
.agent-chat-widget .agent-chat-message-text td {
  padding: 8px 10px;
  border: 1px solid rgba(34, 53, 91, 0.12);
  text-align: left;
  font-size: 12px;
  vertical-align: top;
}

.agent-chat-widget .agent-chat-message-text th {
  background: rgba(34, 53, 91, 0.08);
  font-weight: 700;
}

.agent-chat-widget .agent-chat-message-text a {
  color: #2f7eb9;
  text-decoration: underline;
}

.agent-chat-widget .agent-chat-message-text blockquote {
  border-left: 3px solid rgba(47, 126, 185, 0.35);
  padding-left: 10px;
  color: #52657f;
}
</style>
